/**
 * Passwordless portal-login mode. After the email magic-link verify endpoint sets the
 * httpOnly `fd_portal_session` cookie, it redirects the browser to `/app#portal=1`.
 * JS can't read the httpOnly cookie, so we capture that fragment marker on boot into
 * localStorage as a client-visible flag: it tells the SPA "you're in portal-login mode,
 * don't bounce to Cognito". The cookie stays the real credential — apiFetch sends it
 * automatically (`credentials: "include"`) and `/me` is the server-side source of truth.
 *
 * localStorage (not sessionStorage) so the multi-day session survives new tabs/reloads.
 * Cleared on logout or when a request 401s (the cookie expired server-side).
 */

import { clearCognitoSession } from "./cognitoStorage";

const KEY = "fd_portal_session";

let captured = false;

/** Capture `#portal=1` from the URL once, on app boot. Idempotent. */
export function capturePortalFromUrl(): void {
  if (captured) return;
  captured = true;
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  if (!/[#&]portal=1\b/.test(hash)) return;
  clearCognitoSession();
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    // localStorage unavailable (private mode etc.) — the flag just won't persist.
  }
  // Strip the marker so it doesn't linger in history.
  const cleaned = hash.replace(/([#&])portal=1/, "$1").replace(/^#$|[#&]$/, "");
  const url = window.location.pathname + window.location.search + (cleaned.startsWith("#") ? cleaned : "");
  window.history.replaceState(null, "", url);
}

export function isPortalSession(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPortalSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
